import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaCm030 } from '~/endpoints/ApiTaCm030'
import { ApiTaRe350 } from '~/endpoints/ApiTaRe350'
import { ApiTaRe360 } from '~/endpoints/ApiTaRe360'

// State types
interface MonthList {
  reportType: number
  reportList: {
    reportId: string
    memNum: string
    grade: number
    fiscalYear: number
    attendedMonth: number
    testCountPerMonth: number
    isRead?: boolean
    testType: number
  }[]
}
const getEmptyMonthList = (): MonthList => ({
  reportType: 0,
  reportList: [],
})

interface TestRecord {
  gradeData: {
    memNum: string
    title: string
    results: {
      subjectCode: string
      answerId: string
      score: string
      rankinFlag: number
      ranking: string
      average: string
      deviationValue: string
    }[]
  }
}
const getEmptyTestRecord = (): TestRecord => ({
  gradeData: {
    memNum: '',
    title: '',
    results: [],
  },
})

interface TestSubjectRecord {
  resultsBySubject: {
    memNum: string
    results: {
      questionNo: string
      subQuestionNo: string
      categoryName: string
      difficultyLevel: string
      allocation: string
      score: string
      average: string
      perfectScoreRate: string
    }[]
  }
}
const getEmptyTestSubjectRecord = (): TestSubjectRecord => ({
  resultsBySubject: {
    memNum: '',
    results: [],
  },
})

// State action parameter
interface ActionParamsMonthList {
  reportType: number
  offset: string
  limit: number
}

interface ActionParamsTestRecord {
  testYear: number
  testMonth: number
  times?: number
  grade: number
}

interface ActionParamsTestSubjectRecord {
  subjectCode: string
  fiscalYear: number
  month: number
  times?: number
  grade: number
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/AssignmentTest',
})
export default class AssignmentTest extends VuexModule {
  // States
  private monthList_ = getEmptyMonthList()

  private hasMoreMonthList_ = true

  private testRecord_ = getEmptyTestRecord()

  private testSubjectRecord_ = getEmptyTestSubjectRecord()

  // Getters
  get monthList(): MonthList {
    return this.monthList_
  }

  get hasMoreMonthList(): boolean {
    return this.hasMoreMonthList_
  }

  get testRecord(): TestRecord {
    return this.testRecord_
  }

  get testSubjectRecord(): TestSubjectRecord {
    return this.testSubjectRecord_
  }

  // Mutations
  @Mutation
  updateMonthList(value: MonthList) {
    this.monthList_.reportType = value.reportType
    this.monthList_.reportList = this.monthList_.reportList.concat(value.reportList)
  }

  @Mutation
  updateMonthListReadFlg(index: number) {
    this.monthList_.reportList[index].isRead = true
  }

  @Mutation
  clearMonthList() {
    this.monthList_ = getEmptyMonthList()
  }

  @Mutation
  updateHasMoreMonthList(flg: boolean) {
    this.hasMoreMonthList_ = flg
  }

  @Mutation
  clearHasMoreMonthList() {
    this.hasMoreMonthList_ = true
  }

  @Mutation
  updateTestRecord(value: TestRecord) {
    this.testRecord_ = value
  }

  @Mutation
  updateTestSubjectRecord(value: TestSubjectRecord) {
    this.testSubjectRecord_ = value
  }

  // Actions
  @Action
  clear() {
    this.context.commit('clearMonthList')
    this.context.commit('clearHasMoreMonthList')
    this.context.commit('updateTestRecord', getEmptyTestRecord())
    this.context.commit('updateTestSubjectRecord', getEmptyTestSubjectRecord())
  }

  @Action
  async refreshMonthList(param: ActionParamsMonthList) {
    logger.trace('in: refreshMonthList')
    const api = new ApiTaCm030.Api()

    this.context.commit('updateHasMoreMonthList', false)

    // API: ApiTaCm030
    const result = await api.get<MonthList | null>(
      {
        query: {
          reportType: param.reportType,
          offset: param.offset,
          limit: param.limit,
          courseType: 'T',
        },
      },
      // success
      (data, _) => {
        return {
          reportType: data.reportType,
          reportList: data.reportList,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null || result === undefined) {
      this.context.commit('updateHasMoreMonthList', true)
      return
    }
    this.context.commit('updateHasMoreMonthList', result.reportList.length === param.limit)
    this.context.commit('updateMonthList', result)
  }

  @Action
  setMonthListReadFlg(index: number) {
    this.context.commit('updateMonthListReadFlg', index)
  }

  @Action
  async refreshTestRecord(param: ActionParamsTestRecord) {
    logger.trace('in: refreshTestRecord')
    const api = new ApiTaRe350.Api()
    const request: ApiTaRe350.Get.Request = {
      query: {
        testYear: param.testYear,
        testMonth: param.testMonth,
        grade: param.grade,
      },
    }
    if (param.times) {
      request.query.times = param.times
    }

    // API: ApiTaRe020
    const result = await api.get<TestRecord | null>(
      request,

      // success
      (data, _) => {
        return {
          gradeData: data.gradeData,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateTestRecord', result)
  }

  @Action
  async refreshTestSubjectRecord(param: ActionParamsTestSubjectRecord) {
    logger.trace('in: refreshTestSubjectRecord')
    const api = new ApiTaRe360.Api()
    const request: ApiTaRe360.Get.Request = {
      query: {
        subjectCode: param.subjectCode,
        fiscalYear: param.fiscalYear,
        month: param.month,
        grade: param.grade,
      },
    }
    if (param.times) {
      request.query.times = param.times
    }

    // API: ApiTaRe030
    const result = await api.get<TestSubjectRecord | null>(
      request,

      // success
      (data, _) => {
        return {
          resultsBySubject: data.resultsBySubject,
        }
      },
      // Error
      (data, status) => {
        logger.error(status, data)
        return null
      }
    )
    if (result === null) {
      return
    }
    this.context.commit('updateTestSubjectRecord', result)
  }
}
